import { Doc, toDoc } from '@dadohr/shared/domainTypes/document';
import { EMPTY_OBJ, NOOP } from '@dadohr/shared/domainTypes/emptyConstants';
import firebase from 'firebase/app';
import { useEffect, useState } from 'react';
import { useLoadingValue } from '../../../hooks/useLoadingValue';
import { LoadingValue } from '../../firebase';

type Listener<T> = (self: CollectionListener<T>) => void;
type CollectionListenerOptions = {
  ignoreInitialCachedResults?: boolean;
  /**
   * Skip the live query entirely and resolve to an empty, loaded result.
   * Used for a "no scope" key (e.g. an absent orgId) so a caller gets an empty
   * result without attaching a Firestore listener that can never match.
   */
  disabled?: boolean;
};

export class CollectionListener<T> {
  initialising: boolean = true;
  value: Doc<T>[] | void = undefined;
  error: any | undefined;
  detach: () => void = NOOP;
  _query: firebase.firestore.Query;

  private listeners: Listener<T>[] = [];

  constructor(
    query: firebase.firestore.Query,
    normalize: (s: firebase.firestore.QueryDocumentSnapshot) => Doc<T> = (t) =>
      toDoc<T>(t),
    options: CollectionListenerOptions = EMPTY_OBJ
  ) {
    this._query = query;
    if (options.disabled) {
      // No query attached: present as a loaded, empty collection.
      this.value = [];
      this.initialising = false;
      return;
    }
    this.init(query, normalize, options);
  }

  protected init(
    query: firebase.firestore.Query,
    normalize: (s: firebase.firestore.QueryDocumentSnapshot) => Doc<T> = (t) =>
      toDoc<T>(t),
    options: CollectionListenerOptions
  ) {
    this.detach = query.onSnapshot(
      (snapshot) => {
        if (options.ignoreInitialCachedResults && snapshot.metadata.fromCache) {
          return;
        }
        this.value = snapshot.docs.map(normalize);
        this.error = undefined;
        this.initialising = false;
        this.notify();
      },
      (error) => {
        this.value = undefined;
        this.error = error;
        this.initialising = false;
        this.notify();
      }
    );
  }

  protected notify = () => this.listeners.forEach((l) => l(this));

  listen = (listener: Listener<T>) => {
    this.listeners.push(listener);
    return () => {
      const i = this.listeners.indexOf(listener);
      if (i !== -1) {
        this.listeners.splice(i, 1);
      }
    };
  };

  get = () => {
    return new Promise<Doc<T>[]>((resolve, reject) => {
      const toResult = () => {
        if (this.error) {
          reject(this.error);
          return;
        }
        if (this.value) {
          resolve(this.value);
        }
      };
      if (!this.initialising) {
        toResult();
        return;
      }
      const unlisten = this.listen(() => {
        unlisten();
        toResult();
      });
    });
  };

  asTuple = () => {
    const { value, initialising, error } = this;
    return [value, initialising, error] as [
      typeof value,
      typeof initialising,
      typeof error
    ];
  };

  isQueryEqual = (other: CollectionListener<any>) => {
    return this._query.isEqual(other._query);
  };
}

export const listenToCollections = <T>(
  listeners: CollectionListener<T>[],
  onValue: (docs: Doc<T>[]) => void,
  onError: (err: any) => void
) => {
  const check = () => {
    const error = listeners.find((l) => l.error);
    if (error) {
      onError(error);
      return;
    }
    const values = listeners.map((l) => l.value);
    if (values.every((t) => t)) {
      onValue(values.reduce<Doc<T>[]>((m, v) => m.concat(v || []), []));
    }
  };

  // check NEEDS to be wrapped in an own error function - every listener should
  // have a unique check function, so that deatchment works fine.
  const detachers = listeners.map((l) => l.listen(() => check()));
  check();
  return () => detachers.forEach((detach) => detach());
};

export const useCollectionListeners = <T>(
  listeners: CollectionListener<T>[]
) => {
  const v = useLoadingValue<Doc<T>[]>();
  useEffect(() => {
    if (!v.loading) {
      v.setLoading(true);
    }
    return listenToCollections(
      listeners,
      (ds) => v.setValue(ds),
      (err) => v.setError(err)
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [listeners]);

  return [v.value, v.loading, v.error] as LoadingValue<Doc<T>[]>;
};

export const createCollectionListenerStore = <T>(
  createListener: (key: string) => CollectionListener<T>
) => {
  const store: { [key: string]: CollectionListener<T> } = {};
  return (key: string): CollectionListener<T> => {
    return (store[key] = store[key] || createListener(key));
  };
};

export const createSingleCollectionListenerStore = <T>(
  createListener: () => CollectionListener<T>
) => {
  let listener: CollectionListener<T> | null = null;
  return (): CollectionListener<T> => {
    if (!listener) {
      listener = createListener();
    }
    return listener;
  };
};

export const useCollectionListener = <T>(c: CollectionListener<T>) => {
  const [state, setState] = useState<ReturnType<typeof c.asTuple>>(c.asTuple());
  useEffect(() => {
    const unsub = c.listen((x) => setState(x.asTuple()));
    if (!c.initialising) {
      setState(c.asTuple());
    }
    return unsub;
  }, [c]);
  return state;
};
